import React, { useState, useEffect } from 'react';
import { intl } from 'i18n';
import classNames from 'classnames';

import {
  CareerAppliedFilterFilterOnEnum,
  CareerSearchRequestOrderingEnum,
  CareerSearchResult,
} from 'labxchange-client';
import { Spinner } from 'ui/components';

import CareerCard from '../Careers/CareerCard';
import messages from '../../displayMessages';
import CareerFilterBar from '../CareerFilters/CareerFilterBar';
import {
  buildParentChildFilterOptions,
  buildSimpleFilterOptions,
  CareerTagData,
  fetchCareersData,
} from './career-search';
import NoResults from './NoResults';

const CareerHome: React.FC = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const query = queryParams.get('query');

  const [loading, setLoading] = useState<boolean>(true);
  const [careers, setCareers] = useState<CareerSearchResult[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [count, setCount] = useState<number>(0);
  const [qualificationFilters, setQualificationFilters] = useState<
    CareerTagData[]
  >([]);
  const [selectedQualification, setSelectedQualification] = useState<
    CareerTagData[]
  >([]);
  const [fieldsFilters, setFieldsFilters] = useState<CareerTagData[]>([]);
  const [selectedFields, setSelectedFields] = useState<CareerTagData[]>([]);
  const [subjectFilters, setSubjectFilters] = useState<CareerTagData[]>([]);
  const [selectedSubjects, setSelectedSubjects] = useState<CareerTagData[]>([]);
  const [sortOrder, setSortOrder] = useState<CareerSearchRequestOrderingEnum>(
    CareerSearchRequestOrderingEnum.AlphabeticalAsc
  );

  const updateFiltersIfEmpty = (
    filters: CareerTagData[],
    setFilters: React.Dispatch<React.SetStateAction<CareerTagData[]>>,
    data: any[],
    type: CareerAppliedFilterFilterOnEnum,
    builder: (
      data: any,
      type: CareerAppliedFilterFilterOnEnum
    ) => CareerTagData[]
  ) => {
    if (filters.length === 0) {
      setFilters(builder(data, type));
    }
  };

  const fetchCareers = async (value?: string, page: number = 1) => {
    try {
      setCareers([]);
      setLoading(true);
      const response = await fetchCareersData(
        selectedQualification,
        selectedSubjects,
        selectedFields,
        sortOrder,
        value,
        page
      );
      setCareers(response.results);
      setCurrentPage(page);
      setCount(response.count);
      updateFiltersIfEmpty(
        subjectFilters,
        setSubjectFilters,
        response.aggregations.SubjectArea.buckets,
        CareerAppliedFilterFilterOnEnum.SubjectArea,
        buildParentChildFilterOptions
      );
      updateFiltersIfEmpty(
        qualificationFilters,
        setQualificationFilters,
        response.aggregations.MinimumQualification.buckets,
        CareerAppliedFilterFilterOnEnum.MinimumQualification,
        buildSimpleFilterOptions
      );
      updateFiltersIfEmpty(
        fieldsFilters,
        setFieldsFilters,
        response.aggregations.Category.buckets,
        CareerAppliedFilterFilterOnEnum.Category,
        buildParentChildFilterOptions
      );
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.error('Error fetching careers', error);
      setCareers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCareers(query || '');
  }, [
    query,
    selectedQualification,
    sortOrder,
    selectedSubjects,
    selectedFields,
  ]);

  const onClickLoadMore = async () => {
    await fetchCareers(query || '', currentPage + 1);
  };

  const toggleFilterSelection = (
    selectedFilters: CareerTagData[],
    setSelectedFilters: React.Dispatch<React.SetStateAction<CareerTagData[]>>,
    filter: CareerTagData
  ) => {
    if (selectedFilters.some((selected) => selected.id === filter.id)) {
      let filters = selectedFilters.filter((f) => f.id !== filter.id);
      // Unset child filters if it is a parent filter
      if (filter.childTags) {
        filters = filters.filter(
          (selected) => selected.id.indexOf(filter.id) < 0
        );
      } else {
        filters = filters.filter(
          (selected) => filter.id.indexOf(selected.id) < 0
        );
      }
      setSelectedFilters([...filters]);
    } else {
      let childFilters: CareerTagData[] = [];
      // Select missing child filters if it is a parent filter
      if (filter.childTags) {
        childFilters = filter.childTags?.filter(
          (selected) => !selectedFilters.some((f) => f.id === selected.id)
        );
      }
      setSelectedFilters([...selectedFilters, filter, ...childFilters]);
    }
  };

  const onFilterSelect = (filter: CareerTagData) => {
    switch (filter.type) {
      case CareerAppliedFilterFilterOnEnum.SubjectArea:
        toggleFilterSelection(selectedSubjects, setSelectedSubjects, filter);
        break;
      case CareerAppliedFilterFilterOnEnum.Category:
        toggleFilterSelection(selectedFields, setSelectedFields, filter);
        break;
      case CareerAppliedFilterFilterOnEnum.MinimumQualification:
        toggleFilterSelection(
          selectedQualification,
          setSelectedQualification,
          filter
        );
        break;
    }
  };

  const closeCollapsible = (id: string) => {
    const updatedSubjects = subjectFilters.map((subject) => {
      if (subject.id === id) {
        return { ...subject, opened: !subject.opened };
      }
      return subject;
    });

    setSubjectFilters(updatedSubjects);
  };

  const resetFilters = (
    setSelectedFilters: React.Dispatch<React.SetStateAction<CareerTagData[]>>,
    filters: CareerTagData[],
    close: (id: string) => void
  ) => {
    setSelectedFilters([]);
    filters.forEach((filter) => filter.opened && close(filter.id));
  };

  const resetSelectedSubjects = () =>
    resetFilters(setSelectedSubjects, subjectFilters, closeCollapsible);
  const resetSelectedFields = () =>
    resetFilters(setSelectedFields, fieldsFilters, closeCollapsible);

  const onSortSelection = (optionId: any) => {
    setSortOrder(optionId);
  };

  return (
    <div className='career-home' data-testid='career-home'>
      <CareerFilterBar
        subjectFilters={subjectFilters}
        selectedSubjects={selectedSubjects}
        resetSelectedSubjects={resetSelectedSubjects}
        educationFilters={qualificationFilters}
        selectedEducation={selectedQualification}
        resetSelectedEducation={() => setSelectedQualification([])}
        fieldsFilters={fieldsFilters}
        selectedFields={selectedFields}
        resetSelectedFields={resetSelectedFields}
        resultCount={count}
        selectedSortOrder={sortOrder}
        onSelectOption={onSortSelection}
        onFilterSelect={onFilterSelect}
        closeCollapsible={closeCollapsible}
      />
      <div className='main-careers-list'>
        {careers.map((career) => (
          <CareerCard key={career.slug} career={career} showImage />
        ))}
        {careers.length === 0 && !loading && <NoResults />}
      </div>
      {loading ? (
        <Spinner />
      ) : (
        <>
          {hasNextPage && (
            <button
              type='button'
              className={classNames('load-more-button', {
                'hide-button': !hasNextPage,
              })}
              onClick={onClickLoadMore}
            >
              <span className='ce-label'>
                {intl.formatMessage(messages.careerLoadMoreButton)}
              </span>
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default CareerHome;
